import axiosInstance from './axiosInstance';
import { parseApiError } from '../../common/errors/api-error';
import { getMessageForCode } from '../../common/errors/error-message';

// Request interceptor – no Authorization header (cookies are HttpOnly)
axiosInstance.interceptors.request.use(
    (config) => {
        if (process.env.NODE_ENV === 'development') {
            console.log('API Request:', {
                method: config.method,
                url: config.url,
                data: config.data,
            });
        }

        return config;
    },
    (error) => {
        console.error('Request Error:', error);
        return Promise.reject(error);
    }
);

let refreshPromise = null;

async function tryRefresh() {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
        // Try user refresh first, then staff refresh
        try {
            await axiosInstance.post('/api/auth/refresh', {}, { _skipAuthRefresh: true });
            return true;
        } catch {
            try {
                await axiosInstance.post('/api/admin/auth/refresh', {}, { _skipAuthRefresh: true });
                return true;
            } catch {
                return false;
            }
        } finally {
            refreshPromise = null;
        }
    })();
    return refreshPromise;
}

// Response interceptor
axiosInstance.interceptors.response.use(
    (response) => {
        if (process.env.NODE_ENV === 'development') {
            console.log('API Response:', {
                status: response.status,
                data: response.data,
            });
        }
        return response;
    },
    async (error) => {
        const apiError = parseApiError(error);

        if (error.response) {
            const { status, data } = error.response;
            const code = apiError.code;
            const originalRequest = error.config;

            // Handle 401 with cookie refresh – consistent across all authenticated endpoints, code-based.
            // Use apiError.code to decide auth failure, never status alone; status is fallback only via isAuthError().
            if (apiError.isAuthError() && !originalRequest?._retry && !originalRequest?._skipAuthRefresh) {
                const url = originalRequest?.url || '';
                const isRefreshUrl = url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/verify-otp');
                const isAuthVerifyUrl = url.includes('/auth/verify');
                // Never attempt refresh for refresh/login/verify-otp itself – prevents loops
                if (!isRefreshUrl && !isAuthVerifyUrl) {
                    originalRequest._retry = true;
                    const refreshed = await tryRefresh();
                    if (refreshed) {
                        return axiosInstance(originalRequest);
                    }
                }
                // Refresh failed – consistent redirect to /login for any auth failure (no silent ignoring per endpoint)
                // Avoid redirect loops: not on /login, not on verify endpoints (which callers handle explicitly)
                const isLoginPage = typeof window !== 'undefined' && window.location.pathname.includes('/login');
                if (!isLoginPage && !isAuthVerifyUrl) {
                    if (typeof window !== 'undefined') {
                        window.location.href = '/login';
                    }
                }
            }

            if (apiError.isForbidden() || status === 403) {
                const friendly = getMessageForCode(code, data?.message);
                console.error('Access Forbidden:', friendly, { code, requestId: data?.requestId });
            } else if (status === 404) {
                console.error('Resource Not Found:', getMessageForCode(code, data?.message));
            } else if (status >= 500) {
                console.error('Server Error:', getMessageForCode(code, data?.message), { requestId: data?.requestId });
            }

            console.error('API Error:', {
                code,
                status,
                message: getMessageForCode(code, data?.message),
                requestId: data?.requestId,
                details: data?.details,
            });
        } else if (error.request) {
            console.error('Network Error:', apiError.message, { code: apiError.code });
        } else {
            console.error('Error:', apiError.message);
        }

        error.apiError = apiError;
        error.code = apiError.code;
        return Promise.reject(error);
    }
);

export default axiosInstance;
